import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowLeft, RotateCcw } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Loader, EmptyState } from '@/components/common/Loader'
import { Modal } from '@/components/common/Modal'
import { PatientAvatar } from '@/components/patient/PatientAvatar'
import { Badge } from '@/components/common/Badge'
import { VisitFormFlow } from '@/components/visit-form/VisitFormFlow'
import { useVisitDraftStore } from '@/store/visitDraftStore'
import { patientApi } from '@/api/patientApi'
import { visitApi } from '@/api/visitApi'
import { cleanPayload, hasMeaningfulData, formatMobile, calculateAge, humanize } from '@/lib/utils'

export default function NewVisit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const draftStore = useVisitDraftStore()

  const [patient, setPatient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)

  const draft = draftStore.getDraft(id)
  const [stepIndex, setStepIndex] = useState(draft.stepIndex || 0)
  const [data, setData] = useState(draft.data || {})

  useEffect(() => {
    let alive = true
    setLoading(true)
    patientApi
      .get(id)
      .then((p) => alive && setPatient(p))
      .catch(() => {})
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [id])

  useEffect(() => {
    draftStore.setStepIndex(id, stepIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, id])

  const handleChange = (key, value) => {
    setData((d) => {
      const next = { ...d, [key]: value }
      draftStore.setSectionData(id, key, value)
      return next
    })
  }

  const buildVisitPayload = () => {
    const sections = {}
    const keys = [
      'vitals',
      'history_present_illness',
      'medical_history',
      'personal_history',
      'chief_complaint',
      'dental_history',
      'extra_oral_examination',
      'intra_oral_examination',
      'investigation',
      'provisional_diagnosis',
      'treatment_plan',
      'prescription',
      'consent',
      'doctor_notes',
    ]
    keys.forEach((k) => {
      const v = data[k]
      if (hasMeaningfulData(v)) {
        const cleaned = cleanPayload(v)
        if (cleaned !== undefined) sections[k] = cleaned
      }
    })
    return sections
  }

  const handleSubmit = async () => {
    const payload = buildVisitPayload()
    if (Object.keys(payload).length === 0) {
      toast.error('Please fill at least one section before saving')
      return
    }
    setSubmitting(true)
    try {
      await visitApi.create(id, payload)
      toast.success('Visit recorded')
      draftStore.clearDraft(id)
      navigate(`/patients/${id}`)
    } catch {
      // toast handled
    } finally {
      setSubmitting(false)
    }
  }

  const reset = () => {
    setData({})
    setStepIndex(0)
    draftStore.clearDraft(id)
    setResetOpen(false)
    toast('Draft cleared', { icon: '🧼' })
  }

  if (loading) {
    return (
      <div className="container-page py-10">
        <Loader label="Loading patient…" />
      </div>
    )
  }

  if (!patient) {
    return (
      <div className="container-page py-10">
        <EmptyState
          title="Patient not found"
          action={<Button onClick={() => navigate('/patients')}>Back to patients</Button>}
        />
      </div>
    )
  }

  return (
    <div className="container-page py-6 sm:py-8 lg:py-10">
      <PageHeader
        eyebrow="New consultation"
        title="Record a new visit"
        description="Old visits are never overwritten. This saves a brand new visit record."
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate(`/patients/${id}`)}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back to patient
            </Button>
            <Button
              variant="ghost"
              onClick={() => setResetOpen(true)}
              leftIcon={<RotateCcw className="h-4 w-4" />}
            >
              Reset draft
            </Button>
          </>
        }
      />

      {/* Patient summary */}
      <div className="mt-5 flex items-center gap-3 rounded-2xl bg-white p-3 shadow-soft ring-1 ring-ink-100 sm:p-4">
        <PatientAvatar name={patient.name} id={patient.patient_id} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-ink-900">{patient.name}</p>
          <p className="truncate text-xs text-ink-500">
            <span className="font-mono">{patient.patient_id}</span> ·{' '}
            {patient.age ?? calculateAge(patient.dob)} yrs · {humanize(patient.gender)} ·{' '}
            {formatMobile(patient.mobile_number)}
          </p>
        </div>
        <Badge tone="accent" className="hidden sm:inline-flex">
          New visit
        </Badge>
      </div>

      <div className="mt-6">
        <VisitFormFlow
          data={data}
          onChange={handleChange}
          stepIndex={stepIndex}
          onStepChange={setStepIndex}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Save visit"
        />
      </div>

      <Modal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        title="Clear the draft?"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setResetOpen(false)}>
              Keep
            </Button>
            <Button variant="danger" onClick={reset}>
              Clear draft
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-600">
          Every field you've filled for this visit will be removed. No data has been sent yet.
        </p>
      </Modal>
    </div>
  )
}
